import { clsx } from 'clsx';
import React, { FC, ReactNode } from 'react';

export type ProductGridWrapperProps = {
  children: ReactNode;
  className?: string;
  centerInsufficientColumns?: boolean;
  ffid?: string;
};

/**
 * On very small screens, we assume we are in a container that has only the grid
 * taking up the full width. We want to allow it to use the whole viewport at that
 * size. So we undo our typical 12px margin, and we turn off the gap and switch
 * to space-evenly. The calculation for the cutoff is:
 * 
 *  (2 * <tile_width>) + <page margins:24px> + <gap:12px> + <scrollbar space>
 * 
 * Also, the tile size is set here and not in the tiles because the grid needs to
 * be able to generate empty columns when centerInsufficientColumns is false and
 * there aren't enough products to fill even one row. The grid can't size itself
 * off the contents in that case. An example of this is the product search.
 */
const ProductGridWrapper: FC<ProductGridWrapperProps> = ({
  children,
  ffid,
  className = '',
  centerInsufficientColumns = true,
}) => {
  return (
    <div
      className={clsx(
        '[--product-tile-w:178px] [--product-tile-h:293px]',
        'sm:[--product-tile-w:256px] sm:[--product-tile-h:396px]',
        'grid gap-12 md:gap-24 justify-center justify-items-center',
        'auto-rows-[var(--product-tile-h)]',
        centerInsufficientColumns
          ? 'grid-cols-[repeat(auto-fit,var(--product-tile-w))]'
          : 'grid-cols-[repeat(auto-fill,var(--product-tile-w))]',
        'max-[416px]:-mx-12 max-[416px]:gap-x-0 max-[416px]:justify-evenly',
        className,
      )}
      data-ffid={`ctr-${ffid ? `${ffid}-` : ''}product-grid-wrapper`}
    >
      {children}
    </div>
  );
};

export default ProductGridWrapper;
